import { useEffect, useRef, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Switch,
  Button,
  Row,
  Col,
  InputNumber,
  Tag,
  Typography,
  Alert,
  Space,
  message,
} from "antd";
import { WarningFilled } from "@ant-design/icons";
import {
  add_ai_provider_model,
  update_ai_provider_model,
} from "../../api/admin.apis";
import SmokeTestResult from "./SmokeTestResult";

const { Text } = Typography;

// Try every plausible location for the smoke-test payload — the field may be
// nested under either the success or the error envelope depending on whether
// the create succeeded, failed-on-test, or was bypassed via skip_test.
const extractTestResult = (responseBody) => {
  if (!responseBody) return null;
  return (
    responseBody.data?.test_result ||
    responseBody.test_result ||
    responseBody.data?.test ||
    null
  );
};

const extractErrorPayload = (error) => {
  const data = error?.response?.data;
  if (!data) return { message: error?.message || "Request failed", testResult: null };
  const testResult = extractTestResult(data);
  let msg = data.message;
  if (!msg && data.errors && typeof data.errors === "object") {
    const firstKey = Object.keys(data.errors)[0];
    if (firstKey) msg = data.errors[firstKey];
  }
  return { message: msg || "Request failed", testResult };
};

const ModelFormModal = ({
  visible,
  mode,
  providerKey,
  model,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const isEdit = mode === "edit";

  const [saving, setSaving] = useState(false);
  const [savingAnyway, setSavingAnyway] = useState(false);
  const [failedTestResult, setFailedTestResult] = useState(null);
  const [failureMessage, setFailureMessage] = useState(null);
  const lastPayloadRef = useRef(null);

  useEffect(() => {
    if (visible) {
      if (isEdit && model) {
        form.setFieldsValue({
          model_id: model.model_id ?? "",
          display_name: model.display_name ?? "",
          enabled: model.enabled ?? true,
          input_cost_per_million: model.input_cost_per_million ?? 0,
          output_cost_per_million: model.output_cost_per_million ?? 0,
        });
      } else {
        form.setFieldsValue({
          model_id: "",
          display_name: "",
          enabled: true,
          input_cost_per_million: 0,
          output_cost_per_million: 0,
        });
      }
      setFailedTestResult(null);
      setFailureMessage(null);
      lastPayloadRef.current = null;
    }
  }, [visible, isEdit, model, form]);

  const buildPayload = (values) => {
    if (isEdit) {
      return {
        display_name: values.display_name?.trim() ? values.display_name.trim() : null,
        enabled: values.enabled,
        input_cost_per_million: values.input_cost_per_million ?? 0,
        output_cost_per_million: values.output_cost_per_million ?? 0,
      };
    }
    return {
      model_id: values.model_id?.trim(),
      display_name: values.display_name?.trim() ? values.display_name.trim() : null,
      enabled: values.enabled,
      input_cost_per_million: values.input_cost_per_million ?? 0,
      output_cost_per_million: values.output_cost_per_million ?? 0,
    };
  };

  const announceSuccess = (responseBody, fallbackVerb) => {
    const test = extractTestResult(responseBody);
    if (test?.success && test.latency_ms != null) {
      message.success(
        `${fallbackVerb} — tested in ${(test.latency_ms / 1000).toFixed(2)}s`
      );
    } else {
      message.success(responseBody?.message || fallbackVerb);
    }
  };

  const handleFinish = async (values) => {
    if (!providerKey) return;
    const payload = buildPayload(values);
    lastPayloadRef.current = payload;

    setSaving(true);
    setFailedTestResult(null);
    setFailureMessage(null);

    try {
      let response;
      if (isEdit) {
        // PATCH only updates metadata — no smoke test on edit.
        response = await update_ai_provider_model(providerKey, model.model_id, payload);
      } else {
        // POST runs the smoke test server-side by default.
        response = await add_ai_provider_model(providerKey, payload);
      }
      const body = response.data;
      if (body.status === "success") {
        announceSuccess(body, isEdit ? "Model updated" : "Model added");
        onSaved?.();
        onClose?.();
      } else {
        // Non-2xx caught below; this path is for unexpected success status.
        const test = extractTestResult(body);
        setFailedTestResult(test);
        setFailureMessage(body.message || "Save failed");
      }
    } catch (error) {
      console.error(error);
      const { message: errMsg, testResult } = extractErrorPayload(error);
      if (!isEdit && testResult) {
        // Save was rejected because the automatic smoke test failed.
        setFailedTestResult(testResult);
        setFailureMessage(errMsg);
      } else {
        message.error(errMsg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAnyway = async () => {
    if (isEdit || !providerKey || !lastPayloadRef.current) return;
    setSavingAnyway(true);
    try {
      const response = await add_ai_provider_model(
        providerKey,
        lastPayloadRef.current,
        { skip_test: true }
      );
      const body = response.data;
      if (body.status === "success") {
        message.success(body.message || "Model added (test skipped)");
        onSaved?.();
        onClose?.();
      } else {
        message.error(body.message || "Save failed");
      }
    } catch (error) {
      console.error(error);
      const { message: errMsg } = extractErrorPayload(error);
      message.error(errMsg);
    } finally {
      setSavingAnyway(false);
    }
  };

  const handleAfterClose = () => {
    form.resetFields();
    setFailedTestResult(null);
    setFailureMessage(null);
    lastPayloadRef.current = null;
  };

  return (
    <Modal
      title={
        <span>
          {isEdit ? "Edit Model" : "Add Model"} —{" "}
          <Tag style={{ textTransform: "lowercase" }}>{providerKey}</Tag>
          {isEdit && model?.model_id && (
            <Text code style={{ fontSize: 12 }}>{model.model_id}</Text>
          )}
        </span>
      }
      open={visible}
      onCancel={onClose}
      afterClose={handleAfterClose}
      footer={null}
      destroyOnClose
      width={640}
    >
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <Row gutter={16}>
          <Col span={14}>
            <Form.Item
              label="Model ID"
              name="model_id"
              rules={[{ required: true, message: "Model ID is required" }]}
              extra={
                isEdit
                  ? "Model ID is immutable."
                  : "May contain '/' and ':' (e.g. openai/gpt-oss-120b, deepseek/deepseek-r1-0528:free)."
              }
            >
              <Input placeholder="gpt-4o" disabled={isEdit} />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item label="Display Name" name="display_name">
              <Input placeholder="GPT-4o" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={10}>
            <Form.Item
              label="Input cost / 1M tokens (USD)"
              name="input_cost_per_million"
              initialValue={0}
            >
              <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              label="Output cost / 1M tokens (USD)"
              name="output_cost_per_million"
              initialValue={0}
            >
              <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              label="Enabled"
              name="enabled"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        {!isEdit && (
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
            Saving runs a smoke test against this model. You can bypass the
            test with "Save anyway" if it fails.
          </Text>
        )}

        {/* ── Failed-test inline feedback (create mode only) ────────────── */}
        {!isEdit && (failureMessage || failedTestResult) && (
          <Alert
            type="error"
            showIcon
            icon={<WarningFilled />}
            style={{ marginBottom: 16 }}
            message={failureMessage || "Smoke test failed"}
            description={
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                {failedTestResult && <SmokeTestResult result={failedTestResult} />}
                <div>
                  <Button
                    danger
                    type="primary"
                    size="small"
                    loading={savingAnyway}
                    onClick={handleSaveAnyway}
                  >
                    Save anyway (skip test)
                  </Button>
                </div>
              </Space>
            }
          />
        )}

        <Form.Item style={{ marginBottom: 0 }}>
          <Row gutter={8} justify="end">
            <Col>
              <Button onClick={onClose} disabled={saving || savingAnyway}>
                Cancel
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                htmlType="submit"
                loading={saving}
                disabled={savingAnyway}
              >
                {isEdit ? "Save Changes" : "Save Model"}
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ModelFormModal;
